import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ContrastIcon from '@mui/icons-material/Contrast';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Badge, Box, IconButton, Tooltip } from '@mui/material';
import { MarkDispatchContext, MarkStateContext } from '../../../../context/MarkContext';
import { AppContext } from '../../../../App';
import { IExtractionBatch } from '../../../../types/my-types';
import { useAtom, useAtomValue } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import {
  showAllValidationErrorsAtom,
  forceValidationAtom,
  validationLoadingAtom,
} from '../../../../atoms/spreadsheet-atoms';
import SvgIconValidate from '../../../../images/icons/Glynt_icon_Revalidate.svg?react';
import { hasUndismissedValidationErrors } from '../../../../utils/validationUtils';

const iconStyles = {
  mr: 1,
  fontSize: 16,
};

const statusIconStyles = {
  ml: 'auto',
  fontSize: 16,
};

const textStyles = {
  marginRight: '8px',
};

interface IconValidationSettingsProps {
  extractionBatch?: IExtractionBatch | null;
}

export default function IconValidationSettings({ extractionBatch }: IconValidationSettingsProps) {
  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);
  const {
    validationsEnabled,
    showValidations,
    visibleExtractionIds,
    validationSummaryByExtractionId,
  } = React.useContext(MarkStateContext);
  const { accessibility, setAccessibility } = React.useContext(AppContext);
  const [, setShowAllValidationErrors] = useAtom(showAllValidationErrorsAtom);
  const [, setForceValidation] = useAtom(forceValidationAtom);
  const isValidationLoading = useAtomValue(validationLoadingAtom);
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleDropdownClick = () => {
    if (containerRef.current) setAnchorEl(containerRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleValidationsClick = () => {
    dispatch({
      type: 'SHOW_VALIDATIONS',
      payload: !showValidations,
    });
  };

  const hasErrors =
    extractionBatch?.has_undismissed_validation_errors ??
    hasUndismissedValidationErrors(validationSummaryByExtractionId);

  const hasValidationData =
    validationSummaryByExtractionId.length > 0 &&
    validationSummaryByExtractionId.some((extraction) => extraction.hasValidationResponse);

  // Get badge color based on validation state
  const getBadgeColor = () => {
    // If loading or no validation data yet, show warning
    if (!hasValidationData || isValidationLoading) {
      return 'warning'; // Missing/loading validation data
    }
    if (hasErrors) {
      return 'error'; // Has validation errors
    }
    return 'success'; // No errors
  };

  const handleToggleAccessibilityClick = () => {
    setAccessibility(!accessibility);
  };

  const handleViewValidationErrorsClick = () => {
    setShowAllValidationErrors(true);
    handleClose();
  };

  const handleRevalidateClick = () => {
    setForceValidation(true);
    visibleExtractionIds.forEach((extractionId) => {
      queryClient.removeQueries({
        queryKey: ['extraction-validation', extractionId]
      });
    });
    dispatch({
      type: 'SHOW_VALIDATIONS',
      payload: true,
    });
  };

  const handleMenuRevalidateClick = () => {
    handleRevalidateClick();
    handleClose();
  };

  const isDisabled = !validationsEnabled;

  const iconContent = validationsEnabled && showValidations ? (
    <Badge variant="dot" color={getBadgeColor()}>
      {isValidationLoading ? (
        <CircularProgress size={14} sx={{ color: 'black' }} />
      ) : (
        <SvgIconValidate style={{ color: 'black', width: '14px', height: '14px' }} />
      )}
    </Badge>
  ) : (
    <>
      {isValidationLoading ? (
        <CircularProgress size={14} sx={{ color: 'black' }} />
      ) : (
        <SvgIconValidate style={{ color: 'black', width: '14px', height: '14px' }} />
      )}
    </>
  );

  return (
    <div>
      <Tooltip
        placement="top"
        title="Validation Settings"
        disableFocusListener
        disableTouchListener
      >
        <Box
          ref={containerRef}
          sx={{
            display: 'inline-flex',
            alignItems: 'stretch',
            border: '1px solid #BDC3D2',
            borderRadius: '6px',
            backgroundColor: isDisabled ? '#f5f5f5' : 'white',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            '& svg path': { fill: 'black', stroke: 'black' },
          }}
        >
          <IconButton
            onClick={handleRevalidateClick}
            disabled={isDisabled}
            data-cy="btn-validation-revalidate"
            sx={{
              padding: '8px',
              borderRadius: '6px 0 0 6px',
              '&:hover': { backgroundColor: '#f0f0f0' },
            }}
          >
            {iconContent}
          </IconButton>
          <Box
            sx={{
              width: '1px',
              alignSelf: 'stretch',
              backgroundColor: '#BDC3D2',
            }}
          />
          <IconButton
            onClick={handleDropdownClick}
            disabled={isDisabled}
            id="icon-validation-settings"
            data-cy="btn-validation-settings"
            sx={{
              padding: '0 2px',
              borderRadius: '0 6px 6px 0',
              '&:hover': { backgroundColor: '#f0f0f0' },
            }}
          >
            <ArrowDropDownIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Tooltip>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem
          onClick={handleMenuRevalidateClick}
          disabled={!validationsEnabled}
          data-cy="menu-validation-revalidate"
        >
          <RefreshIcon sx={iconStyles} />
          <span style={textStyles}>Revalidate</span>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleViewValidationErrorsClick}
          disabled={!validationsEnabled}
          data-cy="btn-validation-all-validation-errors"
        >
          <ChecklistRtlIcon sx={iconStyles} />
          <span style={textStyles}>View Validation Errors</span>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleToggleValidationsClick}
          disabled={!validationsEnabled}
          data-cy="menu-validation-show-validations"
        >
          <RemoveRedEyeIcon sx={iconStyles} />
          <span style={textStyles}>Show Validations</span>
          {showValidations ? (
            <CheckIcon sx={{ ...statusIconStyles, color: 'green' }} />
          ) : (
            <CloseIcon sx={{ ...statusIconStyles, color: 'red' }} />
          )}
        </MenuItem>
        <MenuItem
          onClick={handleToggleAccessibilityClick}
          disabled={!validationsEnabled}
          data-cy="menu-validation-enhanced-contrast"
        >
          <ContrastIcon sx={iconStyles} />
          <span style={textStyles}>Enhanced Contrast</span>
          {accessibility ? (
            <CheckIcon sx={{ ...statusIconStyles, color: 'green' }} />
          ) : (
            <CloseIcon sx={{ ...statusIconStyles, color: 'red' }} />
          )}
        </MenuItem>
      </Menu>
    </div>
  );
}
